import { useCallback, useEffect, useState } from 'react';
import { Button, ButtonGroup, Layout, Page, Toast, Banner, Text, Popover, ActionList } from '@shopify/polaris';
import { ExportMinor, RefreshMinor, InfoMinor } from '@shopify/polaris-icons';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../../hooks/redux.js';
import { useAppMutation, useAllCollectionQuery, useProcessingActionsCountQuery, useUndoActionsCountQuery } from '../../../../hooks/index.js';
import { ACTION_TYPE_ATTRIBUTES, ACTION_TYPE_STOCK } from '../../../../../constants/actionTypes.js';
import { openModal } from '../../../../store/features/modal.js';
import { resetAction } from '../../../../store/features/action.js';
import { updateShop } from '../../../../store/features/shop.js';
import { OPEN_MODAL_EXPORT_ACTIONS, OPEN_MODAL_SYNC, OPEN_MODAL_COLLECTION_WARNING } from '../../../../constants/modalTypes.js';
import UndoChangesModal from '../../../modals/UndoChangesModal/UndoChangesModal.js';
import ActionsTable from './components/ActionsTable/ActionsTable.js';
import DashboardActionTabs from './components/DashboardActionTabs/DashboardActionTabs.js';

const tabs = [
  { id: ACTION_TYPE_STOCK, content: 'Stock levels triggered actions' },
  { id: ACTION_TYPE_ATTRIBUTES, content: 'Attributes triggered actions' }
];

const AllActions = () => {
  const dispatch = useAppDispatch();
  const shop = useAppSelector((state) => state.shop.data);
  const [selectedTab, setSelectedTab] = useState(0);
  const [active, setActive] = useState(false);
  const [triggerType, setTriggerType] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUndoing, setIsUndoing] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [popoverActive, setPopoverActive] = useState(false);
  const navigate = useNavigate();

  const { mutate, isLoading } = useAppMutation({
    url: '/api/action/bulk-manual',
    reactMutationOptions: {
      onSuccess() {
        setActive(true);
        setShowBanner(false);
      },
      onError(error) {
        setShowBanner(false);
      }
    }
  });

  const { data: actionsProcessingCount, refetch: refetchActionsProcessingCount } = useProcessingActionsCountQuery({ enabled: false });

  const { data: undoActionIds, refetch: refetchUndoActionsCount } = useUndoActionsCountQuery({ enabled: false });

  const { isFetching: isFetchingCollection, refetch: refetchCollection } = useAllCollectionQuery({
    enabled: false,
    onSuccess: (data: { collection: any }) => {
      if (data?.collection) {
        dispatch(openModal({ modalType: OPEN_MODAL_SYNC, modalData: { triggerType } }));
      } else {
        dispatch(openModal({ modalType: OPEN_MODAL_COLLECTION_WARNING }));
      }
    },
    onError: (e) => {
      console.log('e>', e);
    }
  });

  const toastMarkup = active ? (
    <Toast
      content="Actions started manually"
      onDismiss={() => setActive(false)}
    />
  ) : null;

  const handleCreateAction = useCallback(() => {
    if (shop && (!shop?.productsSynchronization || shop?.synchronizationStatus === 'processing')) {
      setTriggerType('CREATE_ACTION_BUTTON');
      refetchCollection();
    } else {
      dispatch(resetAction());
      navigate('/edit');
    }
  }, [shop]);

  const handleBulkUpdate = useCallback(() => {
    setShowBanner(true);
    mutate({});
  }, [mutate]);

  const handleExport = useCallback(() => {
    dispatch(openModal({ modalType: OPEN_MODAL_EXPORT_ACTIONS }));
  }, []);

  const handleSync = useCallback(() => {
    setTriggerType('');
    refetchCollection();
  }, []);

  const handleSyncStore = useCallback(() => {
    setTriggerType('');
    refetchCollection();
  }, []);

  const { mutate: resetSyncState, isLoading: isResetSyncStateLoading } = useAppMutation({
    url: '/api/shop/reset-sync-state',
    reactMutationOptions: {
      onSuccess: () => {
        // Update shop state in Redux to reflect the reset sync state
        dispatch(updateShop({
          ...shop,
          productsSynchronization: false,
          synchronizationStatus: "waiting"
        }));
      },
      onError: (error) => {
        console.error('Failed to reset sync state:', error);
      }
    }
  });

  const handleResetSyncState = useCallback(() => {
    resetSyncState({});
  }, [resetSyncState]);

  useEffect(() => {
    refetchActionsProcessingCount();
  }, [selectedTab, active, shop]);

  useEffect(() => {
    refetchUndoActionsCount();
  }, [selectedTab, active, shop, isUndoing]);


  const toggleUndoBanner = (show: boolean) => {
    setIsUndoing(show);
  };

  const togglePopoverActive = useCallback(
    () => setPopoverActive((popoverActive) => !popoverActive),
    []
  );

  const activator = (
    <Button onClick={togglePopoverActive} disclosure>
      More actions
    </Button>
  );

  return (
    <Page
      fullWidth
      title="Dashboard"
      primaryAction={{
        disabled: isDeleting,
        content: 'Create new action',
        onAction: handleCreateAction,
      }}
      secondaryActions={
        <Popover
          active={popoverActive}
          activator={activator}
          onClose={togglePopoverActive}
        >
          <ActionList
            items={[
              {
                content: shop?.synchronizationStatus === 'processing' ? 'Syncing products' : 'Sync products',
                onAction: handleSync,
                disabled: isFetchingCollection,
              },
              ...(process.env.STAGING === "true" ? [{
                content: 'Sync Store',
                onAction: handleSyncStore,
                disabled: isFetchingCollection,
              }] : []),
              ...(process.env.STAGING === "true" ? [{
                content: 'Reset sync status',
                onAction: handleResetSyncState,
                disabled: isResetSyncStateLoading,
              }] : []),
              {
                content: 'Export actions',
                onAction: handleExport,
              },
              {
                content: 'Update actions',
                onAction: handleBulkUpdate,
                disabled: isLoading,
              },
            ]}
          />
        </Popover>
      }
    >
      <Layout>
        <Layout.Section>
          {(Boolean(actionsProcessingCount?.count) || showBanner) && (
            <Banner status="warning" icon={InfoMinor}>
              <Text as="p" variant="bodyMd">
                Your action is currently updating. It may take a while depending on the number of products included in the action. Please reload the page after some time to see the updates.
              </Text>
            </Banner>
          )}
          {Boolean(undoActionIds?.actionIds.length || isUndoing) && (
            <Banner status="warning" icon={InfoMinor}>
              <Text as="p" variant="bodyMd">
                Currently undoing changes. It may take time depending on the number of products included in the action. The more products there are the longer it will take.
              </Text>
            </Banner>
          )}
        </Layout.Section>
        <Layout.Section>
          <DashboardActionTabs
            tabs={tabs}
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
          />
        </Layout.Section>
        <Layout.Section>
          <ActionsTable
            undoActionIds={undoActionIds?.actionIds}
            setShowBanner={setShowBanner}
            selectedActionsType={tabs[selectedTab].id}
            active={active}
            handleSync={handleSync}
            handleCreateAction={handleCreateAction}
            isUndoing={isUndoing}
            toggleUndoBanner={toggleUndoBanner}
            setIsDeleting={setIsDeleting}
          />
        </Layout.Section>
      </Layout>
      <UndoChangesModal />
      {toastMarkup}
    </Page>
  );
};

export default AllActions;
